import type {
  Department,
  DepartmentDoctor,
  DepartmentTreatment,
  DepartmentUser,
} from "@/features/clinic/types/department";
import type { Doctor } from "@/features/clinic/types/doctor";
import type { Treatment } from "@/features/clinic/types/treatment";
import type { User } from "@/types/user";

import { createId, createPastTimestamp, pickMultiple } from "./base";

interface DepartmentConfig {
  name: string;
  description: string;
  color: string;
  /** Treatments whose name includes any of these keywords belong to the department. */
  treatmentKeywords: string[];
  /** Treatments whose name includes any of these keywords are excluded. */
  excludeKeywords?: string[];
}

/**
 * Departments for ACME Clinic - mirrors the clinic's doctor
 * specializations so each department groups the relevant treatments, doctors
 * and agents together.
 */
const ACME_DEPARTMENTS: DepartmentConfig[] = [
  {
    name: "Dermatology & Skin Health",
    description:
      "Medical and cosmetic skin care: facials, peels, mesotherapy and rejuvenation for healthy, glowing skin.",
    color: "#14b8a6",
    treatmentKeywords: [
      "facial",
      "skin",
      "derma",
      "meso",
      "peel",
      "nano",
      "booster",
      "acne",
    ],
  },
  {
    name: "Cosmetic Injectables",
    description:
      "Anti-wrinkle, dermal fillers and collagen stimulators delivered by our expert injectors for natural results.",
    color: "#ec4899",
    treatmentKeywords: [
      "botox",
      "filler",
      "sculptra",
      "anti-wrinkle",
      "profhilo",
      "jawline",
    ],
  },
  {
    name: "Laser & Aesthetics",
    description:
      "Advanced laser hair removal, pigmentation and skin-resurfacing using the latest aesthetic laser technology.",
    color: "#8b5cf6",
    treatmentKeywords: ["laser", "tattoo", "pink lips"],
  },
  {
    name: "Hair Restoration",
    description:
      "PRP, mesotherapy and surgical hair-restoration solutions for natural-looking, lasting hair growth.",
    color: "#f59e0b",
    treatmentKeywords: ["prp", "transplant", "hair loss", "fue"],
    excludeKeywords: ["removal"],
  },
  {
    name: "Men's Health & Wellness",
    description:
      "P-Shot, wellness checkups and regenerative treatments focused on men's performance and overall vitality.",
    color: "#0ea5e9",
    treatmentKeywords: ["p-shot", "checkup", "wellness", "men"],
  },
  {
    name: "Lip & Smile Aesthetics",
    description:
      "Lip enhancement, lip blush and pink-lips treatments for fuller, naturally defined and youthful lips.",
    color: "#f43f5e",
    treatmentKeywords: ["lip", "pink lips", "blush", "smile"],
  },
];

/**
 * Match treatments to a department by keyword, falling back to a small random
 * subset so every department always has a few treatments attached.
 */
function selectTreatments(
  treatments: Treatment[],
  config: DepartmentConfig,
): DepartmentTreatment[] {
  const matched = treatments.filter((treatment) => {
    const name = treatment.name.toLowerCase();
    const isExcluded = config.excludeKeywords?.some((keyword) =>
      name.includes(keyword),
    );

    if (isExcluded) {
      return false;
    }

    return config.treatmentKeywords.some((keyword) => name.includes(keyword));
  });

  const selected =
    matched.length > 0 ? matched : pickMultiple(treatments, 1, 3);

  return selected.map((treatment) => ({
    id: treatment.id,
    name: treatment.name,
    media: treatment.media?.map((item) => item.url) ?? [],
  }));
}

/**
 * Generate mock departments for ACME Clinic, wiring together the already
 * generated doctors, treatments and users (agents).
 */
export function generateDepartments(
  organizationId: string,
  workspaceId: string,
  doctors: Doctor[],
  treatments: Treatment[],
  users: User[],
  count: number = ACME_DEPARTMENTS.length,
): Department[] {
  const departments: Department[] = [];
  const departmentsToGenerate = Math.min(count, ACME_DEPARTMENTS.length);

  for (let i = 0; i < departmentsToGenerate; i++) {
    const config = ACME_DEPARTMENTS[i];
    const timestamp = createPastTimestamp(80 - i * 7);

    const departmentDoctors: DepartmentDoctor[] = pickMultiple(
      doctors,
      1,
      3,
    ).map((doctor) => ({
      id: doctor.id,
      name: doctor.name,
      photo: doctor.photo,
    }));

    const departmentUsers: DepartmentUser[] = pickMultiple(users, 1, 3).map(
      (user) => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        imageUrl: user.imageUrl,
      }),
    );

    departments.push({
      id: createId(),
      name: config.name,
      description: config.description,
      color: config.color,
      externalId: null,
      organizationId,
      workspaceId,
      createdAt: timestamp,
      updatedAt: timestamp,
      doctors: departmentDoctors,
      treatments: selectTreatments(treatments, config),
      users: departmentUsers,
    });
  }

  return departments;
}
